import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { Modal } from "../../../../components/modal/Modal";
import { Text } from "../../../../components/Text";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { ThemeType } from "../../../../theme/Theme";
import { standardDate } from "../../../../utils/Date";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";

const Chip = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
  border: 1px solid ${({ theme }) => theme.lightgray};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  p {
    text-decoration: underline;
  }

  &:hover {
    background-color: ${({ theme }) => theme.sub};
    border-color: ${({ theme }) => theme.main};
  }

  &:hover p,
  &:hover svg {
    color: ${({ theme }) => theme.main};
  }
`;

const Dot = styled.span<{ $color: keyof ThemeType }>`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme, $color }) => theme[$color]};
  flex-shrink: 0;
`;

const Row = styled(Flex)`
  justify-content: space-between;
  gap: ${Spacing.m};
  padding-block: ${Spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.lightgray};
`;

const QUALITY_COLOR: Record<string, keyof ThemeType> = {
  GREEN: "success",
  YELLOW: "warning",
  RED: "danger",
};

export const WANumberHealth: React.FC = () => {
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const fetched = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaNumberHealth,
  );

  const integrationId = integration?._id;

  useEffect(() => {
    if (!integrationId) {
      return;
    }

    dispatch(
      integrationActions.getWhatsappMetaNumberHealth({
        userId,
        integrationId,
      }),
    );
  }, [dispatch, integrationId, userId]);

  const health = fetched || integration?.config?.numberHealth;

  const quality =
    health?.qualityRating?.toUpperCase() ||
    integration?.config?.phoneNumberQualityRating?.toUpperCase();

  if (!integration) {
    return null;
  }

  const label = quality
    ? t(`integration.whatsapp.meta.health.quality.${quality.toLowerCase()}`, {
        defaultValue: quality,
      })
    : t("integration.whatsapp.meta.health.quality.unknown");

  const rows = [
    {
      label: t("integration.whatsapp.meta.health.rating"),
      value: label,
    },
    {
      label: t("integration.whatsapp.meta.health.tier"),
      value: health?.messagingLimitTier
        ? t(
            `integration.whatsapp.meta.health.tier.${health.messagingLimitTier}`,
            { defaultValue: health.messagingLimitTier },
          )
        : "",
    },
    {
      label: t("integration.whatsapp.meta.health.status"),
      // Meta keeps adding to these, and a code nobody translated yet still
      // says more than an empty row
      value: health?.status
        ? t(`integration.whatsapp.meta.health.status.${health.status}`, {
            defaultValue: health.status,
          })
        : "",
    },
    {
      label: t("integration.whatsapp.meta.health.nameStatus"),
      value: health?.nameStatus
        ? t(
            `integration.whatsapp.meta.health.nameStatus.${health.nameStatus}`,
            {
              defaultValue: health.nameStatus,
            },
          )
        : "",
    },
    {
      label: t("integration.whatsapp.meta.health.checkedAt"),
      value: health?.checkedAt
        ? standardDate<string>(health.checkedAt, user, true)
        : "",
    },
  ].filter((row) => Boolean(row.value));

  return (
    <>
      <Chip onClick={() => setOpen(true)}>
        <Dot $color={QUALITY_COLOR[quality || ""] || "darkerSub"} />
        <Text fontSize="xs" color="darkerSub">
          {label}
        </Text>
        <AtiraIcon icon={faCircleInfo} size="xs" color="darkerSub" />
      </Chip>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t("integration.whatsapp.meta.health.title")}
      >
        <Flex flexDirection="column" gap="m">
          <Info info={t("integration.whatsapp.meta.health.info")} />

          <Flex flexDirection="column">
            {rows.map((row) => (
              <Row key={row.label}>
                <Text fontSize="s" color="darkerSub">
                  {row.label}
                </Text>
                <Text fontSize="s">{row.value}</Text>
              </Row>
            ))}
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
